import { z } from 'zod'

type IncentiveRefinementInput = {
  type?: 'unique' | 'universal' | 'physical' | 'intersolve' | 'gift_group'
  giftGroupItemIds?: string[] | null
}

const requireGiftGroupItems = (
  data: IncentiveRefinementInput,
  ctx: z.RefinementCtx,
) => {
  if (
    data.type === 'gift_group' &&
    (!data.giftGroupItemIds || data.giftGroupItemIds.length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'gift_group_items_required',
      path: ['giftGroupItemIds'],
    })
  }
}

// Base schema for incentive items (used for both frontend and backend)
const BaseSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'required'),
  picture: z.string().min(1, 'required'),
  price: z.number(),
  description: z.string().min(1, 'required').max(500, 'max_length'),
  categoryId: z
    .string({ required_error: 'required', invalid_type_error: 'required' })
    .min(1, 'required'),
  type: z.enum(
    ['unique', 'universal', 'physical', 'intersolve', 'gift_group'],
    {
      required_error: 'required',
      invalid_type_error: 'required',
    },
  ),
  skuCode: z.string().optional().nullable(),
  digitalCode: z.string().optional().nullable(),
  integrationId: z.string().optional().nullable(),
  voucherCodesFilePath: z.string().optional().nullable(),
  customerId: z.string().optional().nullable(),
  isActive: z.boolean(),
  giftGroupItemIds: z.array(z.string()).optional().nullable(),
})

export const UpdateSchema = BaseSchema.extend({
  id: z.string().min(1, 'required'),
})

const createIncentiveItemFormSchema = (
  isLotteryCampaign?: boolean,
  isIntersolveType?: boolean,
) =>
  BaseSchema.extend({
    skuCode: isLotteryCampaign
      ? z.string().min(1, 'required')
      : z.string().optional().nullable(),
    price: z
      .string()
      .min(1, 'required')
      .regex(/^[\d]+\.{0,1}[\d]{0,2}$/, 'invalid_number'),
    integrationId: isIntersolveType
      ? z
          .string({
            required_error: 'required',
            invalid_type_error: 'required',
          })
          .min(1, { message: 'required' })
      : z.string().optional().nullable(),
  }).superRefine(requireGiftGroupItems)
const updateIncentiveItemFormSchema = (
  isLotteryCampaign?: boolean,
  isIntersolveType?: boolean,
) =>
  BaseSchema.extend({
    id: z.string().min(1, 'required'),
    skuCode: isLotteryCampaign
      ? z.string().min(1, 'required')
      : z.string().optional().nullable(),
    price: z.any().optional().nullable(),
    categoryId: z.any().optional().nullable(),
    integrationId: isIntersolveType
      ? z
          .string({
            required_error: 'required',
            invalid_type_error: 'required',
          })
          .min(1, { message: 'required' })
      : z.string().optional().nullable(),
  }).superRefine(requireGiftGroupItems)

// API schemas with coercion for backend (ts-rest contracts)
// These accept HTML form data (strings) and coerce to proper types
const CreateIncentiveItemApiSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().min(1, 'required'),
    picture: z.string().min(1, 'required'),
    price: z.preprocess(
      (val) => (typeof val === 'string' ? parseFloat(val) : val),
      z.number(),
    ),
    description: z.string().min(1, 'required').max(500, 'max_length'),
    categoryId: z
      .string({ required_error: 'required', invalid_type_error: 'required' })
      .min(1, 'required'),
    type: z.enum(
      ['unique', 'universal', 'physical', 'intersolve', 'gift_group'],
      {
        required_error: 'required',
        invalid_type_error: 'required',
      },
    ),
    skuCode: z.string().optional().nullable().default(null),
    digitalCode: z.string().optional().nullable().default(null),
    integrationId: z.string().optional().nullable().default(null),
    voucherCodesFilePath: z.string().optional().nullable().default(null),
    customerId: z.string().optional().nullable().default(null),
    isActive: z.boolean(),
    campaignId: z.string().optional(),
    isAvailable: z.boolean().optional(),
    giftGroupItemIds: z.array(z.string()).optional().nullable().default(null),
  })
  .superRefine(requireGiftGroupItems)

const UpdateIncentiveItemApiSchema = CreateIncentiveItemApiSchema.innerType()
  .omit({ isAvailable: true })
  .extend({ id: z.string().min(1, 'required') })
  .superRefine(requireGiftGroupItems)

type CreateIncentiveItemType = z.infer<typeof BaseSchema>

// Form-specific type (price is string for HTML inputs)
type IncentiveItemFormType = Omit<CreateIncentiveItemType, 'price' | 'type'> & {
  price: string | number
  type: 'unique' | 'universal' | 'physical' | 'intersolve' | 'gift_group' | ''
}

export {
  CreateIncentiveItemApiSchema,
  UpdateIncentiveItemApiSchema,
  createIncentiveItemFormSchema,
  updateIncentiveItemFormSchema,
}
export type { IncentiveItemFormType }
